import Loader from 'Components/Common/Loader';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { createCampaignFromIdea, fetchCampaignIdeas, fetchCampaignStrategies, fetchCampaignStrategy, generateCampaignIdeas } from 'services/api/campaign';
import { AppDispatch, RootState } from 'store';
import { CampaignIdea, CampaignStrategy } from 'types/campaign';
import { getErrorMessage } from 'utils/helpers';
import '../../../pages/Campaigns/style.scss';
import { Link, useNavigate } from 'react-router-dom';
import { UserBrand } from 'types/users';
import { fetchUserBrands as fetchUserBrandsThunk } from 'slices/users/brands/thunk';
import AICampaignCreationLayout from 'Layouts/AICampaignCreationLayout';
import AICampaignIdeas from './AICampaignIdeas';
import { useFirebase } from 'contexts/FirebaseAuthContext';
import { collection, doc, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import AILoader from 'Components/Loader/AILoader';
import { parse } from 'best-effort-json-parser';

const POLL_INTERVAL = 10000; //10 seconds

const CampaignCreationAIStrategies: React.FC<{ brandSocialAccountId: string | number, onBrandChange?: (brand: UserBrand | null) => void }> = ({ brandSocialAccountId, onBrandChange }) => {
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const dispatch: AppDispatch = useDispatch();
    const { realtimeUpdatesDB } = useFirebase();
    const [campaignStrategies, setCampaignStrategies] = useState<CampaignStrategy[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedStrategyUuid, setSelectedStrategyUuid] = useState<string>('');
    const [brandMenuOpen, setBrandMenuOpen] = useState(false);
    const brandMenuRef = React.useRef<HTMLDivElement | null>(null);
    const [generatingIdeas, setGeneratingIdeas] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [newIdeasAvailable, setNewIdeasAvailable] = useState(false);
    const [selectedIdea, setSelectedIdea] = useState<CampaignIdea | null>(null);
    const [fetchingIdeas, setFetchingIdeas] = useState(false);
    const [aiTaskUUID, setAiTaskUUID] = useState<string | null>(null);
    const [aiTaskChipText, setAiTaskChipText] = useState<string | null>(null);
    const [aiTaskStatus, setAiTaskStatus] = useState<string | null>(null);
    const [aiTaskResult, setAiTaskResult] = useState<any | null>(null);
    const [newIdeasPartiallyAvailable, setNewIdeasPartiallyAvailable] = useState(false);

    const [campaignIdeas, setCampaignIdeas] = useState<CampaignIdea[]>([]);
    const [creatingCampaign, setCreatingCampaign] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(fetchUserBrandsThunk(false));
    }, [dispatch]);

    useEffect(() => {
        if (!brandMenuOpen) return;
        const onDocMouseDown = (e: MouseEvent) => {
            const el = brandMenuRef.current;
            if (!el) return;
            if (e.target instanceof Node && el.contains(e.target)) return;
            setBrandMenuOpen(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [brandMenuOpen]);

    useEffect(() => {
        if (!aiTaskUUID || !realtimeUpdatesDB) return;

        if (aiTaskResult) {
            return;
        };

        const q = query(
            collection(realtimeUpdatesDB, "ai_tasks", aiTaskUUID, "events"),
            orderBy('updated_at', 'desc'),
            limit(1)
        );


        const unsubscribe = onSnapshot(q, (snapshot) => {
            const newMessages: any = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setAiTaskChipText(newMessages[0]?.event ?? null);
        });

        return () => unsubscribe();
    }, [aiTaskUUID, realtimeUpdatesDB, aiTaskResult]);

    useEffect(() => {
        if (!aiTaskUUID || !realtimeUpdatesDB) return;
        if (aiTaskStatus === 'completed') {
            setGeneratingIdeas(false);
            fetchIdeas();
            return;
        }
        setGeneratingIdeas(true);

        const unsubscribe = onSnapshot(
            doc(realtimeUpdatesDB, 'ai_tasks', aiTaskUUID),
            (snapshot) => {
                if (!snapshot.exists()) return;

                const data = snapshot.data();

                setAiTaskStatus(data?.status);
                setAiTaskResult(data?.result);
            },
            (error) => {
                console.error("doc listener error:", error);
            }
        );

        return () => unsubscribe();
    }, [aiTaskUUID, realtimeUpdatesDB, aiTaskStatus]);

    useEffect(() => {
        if (!aiTaskResult) return;
        const jsonResult = parse(aiTaskResult);
        const ideas = Array.isArray(jsonResult?.campaigns) ? jsonResult?.campaigns : [];
        setCampaignIdeas(ideas.map((i: CampaignIdea, index: number) => ({ uuid: index, campaign: i })));
        setNewIdeasPartiallyAvailable(ideas.length > 0);
    }, [aiTaskResult])

    const setSelectedStrategy = (strategy: CampaignStrategy) => {
        setCampaignStrategies((cs) => cs.map((c) => c.uuid === strategy.uuid ? strategy : c));
    }


    const createCampaign = async () => {
        if (!selectedIdea || !selectedAgencyId) return;
        setCreatingCampaign(true);
        try {
            const response = await createCampaignFromIdea(selectedAgencyId, selectedIdea.uuid);
            if (response.campaign?.uuid) {
                navigate(`/campaign/${response.campaign.uuid}/edit`);
            }

        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to create campaign'));
        } finally {
            setCreatingCampaign(false);
        }
    }

    const fetchIdeas = async (strategyUuid: string = selectedStrategyUuid) => {
        if (!selectedAgencyId || !brandSocialAccountId || !strategyUuid) return;
        setFetchingIdeas(true);
        try {
            const response = await fetchCampaignIdeas(selectedAgencyId, brandSocialAccountId, strategyUuid);
            setCampaignIdeas(response?.items ?? []);
            setNewIdeasAvailable(true);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch campaign ideas'));
        } finally {
            setFetchingIdeas(false);
        }
    }

    const fetchStrategy = async () => {
        if (!selectedAgencyId || !brandSocialAccountId || !selectedStrategy) return;
        try {
            const updatedStrategy = await fetchCampaignStrategy(selectedAgencyId, brandSocialAccountId, selectedStrategy.uuid);
            if (updatedStrategy && selectedStrategy?.uuid === updatedStrategy.uuid) {
                // console.log('updatedStrategy', updatedStrategy);
                // console.log('selectedStrategy', selectedStrategy);
                setSelectedStrategy(updatedStrategy);
                // console.log('new Date(updatedStrategy.ideas_generated_at ?? 0).getTime()', new Date(updatedStrategy.ideas_generated_at ?? 0).getTime());
                // console.log('new Date(selectedStrategy.ideas_generated_at ?? 0).getTime()', new Date(selectedStrategy.ideas_generated_at ?? 0).getTime());
                // console.log('new Date(updatedStrategy.ideas_generated_at ?? 0).getTime() > new Date(selectedStrategy.ideas_generated_at ?? 0).getTime()', new Date(updatedStrategy.ideas_generated_at ?? 0).getTime() > new Date(selectedStrategy.ideas_generated_at ?? 0).getTime());
                if ((new Date(updatedStrategy.ideas_generated_at ?? 0).getTime()) > new Date(selectedStrategy.ideas_generated_at ?? 0).getTime()) {
                    setNewIdeasAvailable(true);
                    // console.log('new ideas available');
                    // console.log('fetching ideas');
                    fetchIdeas();
                } else {
                    // console.log('no new ideas available');
                    setTimeout(() => { fetchStrategy() }, POLL_INTERVAL);
                }
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch campaign strategy'));
        }
    }



    const fetchStrategies = async () => {
        if (!selectedAgencyId || !brandSocialAccountId) return;
        try {
            setLoading(true);
            const response = await fetchCampaignStrategies(selectedAgencyId, brandSocialAccountId);
            const items = (response as any)?.items ?? (response as any)?.data ?? response;
            if (Array.isArray(items)) {
                setCampaignStrategies(items);
                if (!selectedStrategyUuid && items[0]?.uuid) setSelectedStrategyUuid(items[0].uuid);
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch campaign strategies'));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!selectedAgencyId || !brandSocialAccountId) return;
        fetchStrategies();
    }, [brandSocialAccountId, selectedAgencyId]);


    const selectedStrategy = useMemo(() => {
        return campaignStrategies.find((s: CampaignStrategy) => s.uuid === selectedStrategyUuid) ?? campaignStrategies[0] ?? null;
    }, [campaignStrategies, selectedStrategyUuid]);

    // useEffect(() => {
    //     if (generatingIdeas && brandSocialAccountId && selectedAgencyId) {
    //         setTimeout(() => { fetchStrategy() }, POLL_INTERVAL);
    //     }
    // }, [generatingIdeas, brandSocialAccountId, selectedAgencyId])



    const generateIdeas = async () => {
        if (!selectedStrategy || !selectedAgencyId || !brandSocialAccountId) return;
        if (selectedStrategy.ideas_generated_at) {
            setNewIdeasAvailable(true);
            fetchIdeas();
            return;
        }
        setGenerating(true);
        try {
            const response = await generateCampaignIdeas(selectedAgencyId, brandSocialAccountId, selectedStrategy.uuid); // This response *
            if (response.strategy?.stream_uuid) {
                setAiTaskUUID(response.strategy.stream_uuid);
            }
            setGeneratingIdeas(true);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to generate campaign ideas'));
            setGeneratingIdeas(false);
        } finally {
            setGenerating(false);
        }
    }

    const resetState = () => {
        setNewIdeasAvailable(false);
        setGeneratingIdeas(false);
        setGenerating(false);
        setFetchingIdeas(false);
        setCreatingCampaign(false);
        setAiTaskUUID(null);
        setAiTaskChipText(null);
        setAiTaskStatus(null);
        setAiTaskResult(null);
        fetchStrategies();
        setNewIdeasPartiallyAvailable(false);
    }

    const showCampaignIdeas = useMemo(() => {
        return newIdeasPartiallyAvailable || newIdeasAvailable;
    }, [newIdeasPartiallyAvailable, newIdeasAvailable]);

    const getContentStyles = (strategy: CampaignStrategy) => {
        const contentStyle = strategy?.strategy?.content_style ?? '';
        const contentStyles = strategy?.strategy?.content_styles ?? [];
        const mergedContentStyles = [];
        if (contentStyle) {
            mergedContentStyles.push(contentStyle);
        }
        if (contentStyles.length > 0) {
            mergedContentStyles.push(...contentStyles);
        }
        return mergedContentStyles.join(' • ');
    };

    if (generatingIdeas && !newIdeasAvailable && !newIdeasPartiallyAvailable) {
        return (
            <AILoader chipText={aiTaskChipText ?? 'Analysing with AI'} />
        );
    }

    if (loading) return <Loader />

    return (
        <AICampaignCreationLayout
            onBackClick={() => showCampaignIdeas ? resetState() : navigate('/campaign/new')}
            mode="strategy"
            steps={[
                {
                    label: 'Choose Strategy',
                    title: 'Choose a Strategy',
                    subtitle: 'Each campaign is a ready-to-deploy content brief aligned with this strategy.',
                    active: !showCampaignIdeas,
                    completed: showCampaignIdeas,
                },
                {
                    label: 'Choose Campaign',
                    title: 'Choose a Campaign',
                    subtitle: 'Each campaign is a ready-to-deploy content brief aligned with this strategy.',
                    active: showCampaignIdeas,
                    completed: false,
                },
                {
                    label: 'Review',
                    title: 'Review',
                    subtitle: 'Review the campaign and make any final adjustments.',
                    active: false,
                    completed: false,
                }
            ]}
            brandSocialAccountId={brandSocialAccountId}
            onBrandChange={onBrandChange}
            bottomBarContent={
                (showCampaignIdeas) ? (
                    selectedIdea?.generated_campaign?.uuid ? (
                        <Link
                            className="btn btn-gradient"
                            to={`/campaign/${selectedIdea?.generated_campaign?.uuid}`}
                            target="_blank"
                        >
                            View Campaign
                        </Link>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-gradient"
                            disabled={!selectedIdea || creatingCampaign || !newIdeasAvailable}
                            onClick={createCampaign}
                        >
                            {creatingCampaign ? 'CREATING CAMPAIGN...' : (generatingIdeas ? 'GENERATING IDEAS...' : 'USE THIS CAMPAIGN SETUP')}
                        </button>
                    )
                ) : (
                    <button
                        type="button"
                        className="btn btn-ai-gradient px-5"
                        disabled={!selectedStrategy || generating}
                        onClick={generateIdeas}
                    >
                        {generating ? 'PLEASE WAIT...' : 'USE THIS STRATEGY'}
                    </button>
                )
            }
        >
            {
                showCampaignIdeas ? (<AICampaignIdeas loading={fetchingIdeas} generating={generatingIdeas} campaignIdeas={campaignIdeas} onIdeaSelect={setSelectedIdea} />) :
                    (
                        campaignStrategies.length > 0 ? (
                            <div className="campaign-ai-creation__grid">
                                <div className="campaign-ai-creation__left" role="listbox" aria-label="Strategy list">
                                    {campaignStrategies.map((s) => {
                                        const selected = s.uuid === selectedStrategyUuid;
                                        return (
                                            <button
                                                key={s.uuid}
                                                type="button"
                                                className={`campaign-ai-creation-item${selected ? ' campaign-ai-creation-item--selected' : ''}`}
                                                onClick={() => setSelectedStrategyUuid(s.uuid)}
                                                role="option"
                                                aria-selected={selected}
                                            >
                                                <div className="campaign-ai-creation-item__name">{s.name}</div>
                                                <div className="campaign-ai-creation-item__subline">{getContentStyles(s)}</div>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="campaign-ai-creation__right" aria-label="Strategy details">
                                    {selectedStrategy && (
                                        <div className="campaign-ai-creation-detail">
                                            <div className="campaign-ai-creation-detail__title">{selectedStrategy.name}</div>

                                            <div className="campaign-ai-section">
                                                <div className="campaign-ai-section__label">THE PITCH</div>
                                                <div className="campaign-ai-field">{selectedStrategy.pitch}</div>
                                            </div>

                                            <div className="campaign-ai-miniGrid" aria-label="Creator tier and hooks">
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="campaign-ai-miniGrid-label">CREATOR TIER</div>
                                                    <div className="campaign-ai-miniGrid-value">{selectedStrategy.strategy?.creator_criteria?.tier_focus ?? '—'}</div>
                                                </div>
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="campaign-ai-miniGrid-label">HOOKS</div>
                                                    <div className="campaign-ai-miniGrid-value">{selectedStrategy.strategy.hooks.join(' • ')}</div>
                                                </div>
                                                <div></div>
                                            </div>

                                            <div className="campaign-ai-section">
                                                <div className="campaign-ai-section__label">TARGET AUDIENCE</div>
                                                <div className="campaign-ai-card">
                                                    <div className="campaign-ai-card__row">
                                                        <div className="campaign-ai-subcard">
                                                            <div className="campaign-ai-subcard__k">PRIMARY SEGMENT</div>
                                                            <div className="campaign-ai-subcard__v">{selectedStrategy.strategy?.target_demography?.primary_segment ?? '—'}</div>
                                                        </div>
                                                        <div className="campaign-ai-subcard">
                                                            <div className="campaign-ai-subcard__k">SECONDARY SEGMENT</div>
                                                            <div className="campaign-ai-subcard__v">{selectedStrategy.strategy?.target_demography?.secondary_segment ?? '—'}</div>
                                                        </div>
                                                    </div>
                                                    <div className="campaign-ai-subcard campaign-ai-subcard--full">
                                                        <div className="campaign-ai-subcard__k">PSYCHOGRAPHIC PROFILE</div>
                                                        <div className="campaign-ai-subcard__v">{selectedStrategy.strategy?.target_demography?.psychographic_profile ?? '—'}</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="campaign-ai-section">
                                                <div className="campaign-ai-section__label">CREATOR PROFILE</div>
                                                <div className="campaign-ai-field">{selectedStrategy.strategy?.creator_criteria?.qualitative_traits ?? '—'}</div>
                                            </div>

                                            <div className="campaign-ai-section">
                                                <div className="campaign-ai-section__label">30-60 DAYS EXECUTION PLAN</div>
                                                <div className="campaign-ai-field">{selectedStrategy.strategy?.social_media_strategy_30_60_days ?? '—'}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="campaign-ai-strategy d-flex flex-column align-items-center justify-content-center vh-50">
                                <div className="text-muted" style={{ fontFamily: 'Rubik, sans-serif' }}>No strategies found</div>
                            </div>
                        )
                    )
            }

        </AICampaignCreationLayout>
    )

}

export default CampaignCreationAIStrategies